/**
 * Pure helpers for the Quality Data Package screens. CR-2026-QDP (2026-10-14). Exported for tests.
 * The server decides every rule; these only arrange what it returns.
 */
export const STATE_LABEL = {
  DRAFT: "Draft", IN_TECHNICAL_REVIEW: "In technical review", REVIEWED: "Reviewed", APPROVED: "Approved (QM)",
  ISSUED: "Issued", SUPERSEDED: "Superseded", WITHDRAWN: "Withdrawn",
};
export const STATE_COLOR = { DRAFT: "default", IN_TECHNICAL_REVIEW: "warning", REVIEWED: "info", APPROVED: "info", ISSUED: "success", SUPERSEDED: "default", WITHDRAWN: "default" };
export const SEVERITY = {
  BLOCK: { label: "Blocks", color: "error", help: "Fix it in the records. It cannot be excepted." },
  EXCEPTIONABLE: { label: "Needs exception", color: "warning", help: "Declare an exception with a reason, or fix it in the records." },
  DISCLOSED: { label: "Disclosed", color: "default", help: "Printed in Section 10 automatically." },
};

/** Gaps with their answer state: answered by a declared exception (same gap key) or not. */
export function gapRows(content, exceptions) {
  const ex = new Map((exceptions || []).map((e) => [e.gapKey, e]));
  return ((content && content.gaps) || []).map((g) => ({ ...g, exception: ex.get(g.key) || null,
    open: g.severity === "BLOCK" || (g.severity === "EXCEPTIONABLE" && !ex.has(g.key)) }));
}

/** Which actions this viewer may try now. The server re-checks every one, including the two-person rule. */
export function actionsFor(pkg, perms, userName) {
  const has = (p) => perms.includes(p);
  const s = pkg?.state;
  const isAuthor = !!userName && [pkg?.builtByName, pkg?.lastBuiltByName].includes(userName);
  return {
    rebuild: s === "DRAFT" && has("build_qdp"),
    declare: s === "DRAFT" && has("build_qdp"),
    submit: s === "DRAFT" && has("build_qdp"),
    withdraw: s === "DRAFT" && has("build_qdp"),
    review: s === "IN_TECHNICAL_REVIEW" && has("review_qdp") && !isAuthor,
    approve: s === "REVIEWED" && has("approve_qdp") && !isAuthor,
    issue: s === "APPROVED" && has("approve_qdp"),
  };
}

/** The public verify URL printed as the QR on the cover. */
export const verifyUrl = (token, origin = "https://shopfloormgmt.com") => `${origin}/qdp/verify/${token}`;

/** Section 10 lines: declared exceptions, disclosed gaps, stated limitations. Always present, even when "None". */
export function section10(content, exceptions) {
  const lines = [];
  for (const e of exceptions || []) lines.push({ kind: "Exception", text: `${e.gapCode} ${e.where || ""}`.trim(), detail: e.reason });
  for (const g of ((content && content.gaps) || []).filter((x) => x.severity === "DISCLOSED")) lines.push({ kind: "Disclosed", text: `${g.code} ${g.where || ""}`.trim(), detail: g.text });
  for (const l of (content && content.limitations) || []) lines.push({ kind: "Limitation", text: "", detail: l });
  return lines;
}

/** CR-2026-QDP-PDF: the issued file's name, the same as the server's Content-Disposition. */
export const pdfFileName = (pkg) => `${String(pkg.number || "QDP").replace(/[^A-Za-z0-9._-]/g, "_")}_Rev${Number(pkg.revision) || 0}.pdf`;
/** Only an issued (or later superseded) package has a PDF; drafts print from the page with their DRAFT banner. */
export const hasPdf = (pkg) => ["ISSUED", "SUPERSEDED"].includes(pkg && pkg.state);
/** CR-2026-QDP-ZIP: the turnover ZIP's name, the same as the server's Content-Disposition. */
export const zipFileName = (pkg) => pdfFileName(pkg).replace(/\.pdf$/, "_turnover.zip");
